/**
 * Gründerportalen (docs/qa/rettigheter/GRUNDER.md, del B; migrering 0476).
 *
 * `/v1/platform/permission-options` — hvilke funksjoner organisasjonene kan justere på sin
 * Rettigheter-side. Bare for gründeren (`app.is_platform_admin()`); alle andre får 404, ikke 403,
 * fordi portalen ikke skal finnes for dem.
 *
 * Kodens standard er `ADJUSTABLE_ACTIONS` (malens `adjustable`); gründerens valg vinner. Et valg
 * satt tilbake til standarden slettes, så `adjustable !== defaultAdjustable` er «Endret fra
 * standard». Låste funksjoner kan aldri bli justerbare.
 */
import { z } from 'zod';

import { isKnownAction } from '../policy/rules.js';

const CELLS = ['yes', 'own', 'dept', 'part', 'grant', 'no', 'locked'] as const;

/** En rolles standard i malen, som den står i `ROLES.md` §4 (også `locked`). */
export const platformDefaultCellSchema = z.enum(CELLS).meta({ id: 'PlatformDefaultCell' });

export const platformPermissionOptionSchema = z
  .object({
    /** `resource:verb`, f.eks. `contact:delete`. */
    action: z.string().refine(isKnownAction, { message: 'Unknown action.' }),
    resource: z.string(),
    verb: z.string(),
    /** Den effektive verdien: gründerens valg, ellers kodens standard. */
    adjustable: z.boolean(),
    /** Kodens standard (`ADJUSTABLE_ACTIONS`). */
    defaultAdjustable: z.boolean(),
    /** En av de tre som aldri kan justeres. Da er `adjustable` alltid false. */
    locked: z.boolean(),
    /** Innstillingen i `PERMISSION_SECTIONS` funksjonen står i, eller null («Andre funksjoner»). */
    settingId: z.string().nullable(),
    sectionId: z.string().nullable(),
    defaults: z.object({
      superadmin: platformDefaultCellSchema,
      admin: platformDefaultCellSchema,
      member: platformDefaultCellSchema,
      limited: platformDefaultCellSchema,
    }),
    /**
     * Det en NY organisasjon får (sak 239.38, 0540): plattformens valg, ellers malen. Superadmin er
     * alltid sann. Eksisterende organisasjoner røres ikke av dette.
     */
    newOrganization: z.object({
      superadmin: z.boolean(),
      admin: z.boolean(),
      member: z.boolean(),
      limited: z.boolean(),
    }),
  })
  .meta({ id: 'PlatformPermissionOption' });

/** Alle funksjonene i malen, i malens rekkefølge. */
export const platformPermissionOptionsResponseSchema = z
  .object({ items: z.array(platformPermissionOptionSchema) })
  .meta({ id: 'PlatformPermissionOptionsResponse' });

export const platformPermissionOptionChangeSchema = z
  .object({
    action: z.string().refine(isKnownAction, { message: 'Unknown action.' }),
    adjustable: z.boolean(),
  })
  .meta({ id: 'PlatformPermissionOptionChange' });

/**
 * 1–100 endringer, én transaksjon. En innstilling med flere funksjoner slås av og på samlet: API-et
 * avviser (`setting_split`) en endring som ville gitt funksjonene i én innstilling ulik verdi, så
 * weben sender alle. Samme funksjon to ganger avvises av skjemaet.
 */
export const updatePlatformPermissionOptionsRequestSchema = z
  .object({
    changes: z
      .array(platformPermissionOptionChangeSchema)
      .min(1)
      .max(100)
      .refine(
        (changes) => new Set(changes.map((change) => change.action)).size === changes.length,
        { message: 'The same action appears more than once.' },
      ),
  })
  .meta({ id: 'UpdatePlatformPermissionOptionsRequest' });

/** Rollene plattformen kan sette standarden for. Superadmin har alltid alt (0474). */
export const PLATFORM_DEFAULT_ROLES = ['admin', 'member', 'limited'] as const;

export const platformDefaultPermissionChangeSchema = z
  .object({
    action: z.string().refine(isKnownAction, { message: 'Unknown action.' }),
    role: z.enum(PLATFORM_DEFAULT_ROLES),
    allowed: z.boolean(),
  })
  .meta({ id: 'PlatformDefaultPermissionChange' });

/**
 * Standarden for NYE organisasjoner, 1–200 celler i én transaksjon (sak 239.38). Samme celle to
 * ganger avvises av skjemaet. En låst funksjon avvises av API-et (`action_locked`).
 */
export const updatePlatformDefaultPermissionsRequestSchema = z
  .object({
    changes: z
      .array(platformDefaultPermissionChangeSchema)
      .min(1)
      .max(200)
      .refine(
        (changes) =>
          new Set(changes.map((change) => change.action + '/' + change.role)).size ===
          changes.length,
        { message: 'The same cell appears more than once.' },
      ),
  })
  .meta({ id: 'UpdatePlatformDefaultPermissionsRequest' });

export type PlatformDefaultPermissionChange = z.infer<typeof platformDefaultPermissionChangeSchema>;
export type UpdatePlatformDefaultPermissionsRequest = z.infer<
  typeof updatePlatformDefaultPermissionsRequestSchema
>;
export type PlatformDefaultCell = z.infer<typeof platformDefaultCellSchema>;
export type PlatformPermissionOption = z.infer<typeof platformPermissionOptionSchema>;
export type PlatformPermissionOptionsResponse = z.infer<
  typeof platformPermissionOptionsResponseSchema
>;
export type PlatformPermissionOptionChange = z.infer<typeof platformPermissionOptionChangeSchema>;
export type UpdatePlatformPermissionOptionsRequest = z.infer<
  typeof updatePlatformPermissionOptionsRequestSchema
>;
